import { Component, Input, OnInit, inject, DestroyRef } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { translate } from '@jsverse/transloco';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { GenericErrorComponent } from 'src/app/components/generic-error/generic-error.component';
import { Constants } from 'src/app/models/constants';
import { SolutionsService } from 'src/app/services/api/solutions.service';
import { SidenavService } from 'src/app/services/util/sidenav.service';
import { CustomToastrService } from 'src/app/services/util/toastr.service';
import { ConfirmationDialogComponent } from 'src/app/components/confirmation-dialog/confirmation-dialog.component';
import { PathToLiveEnvironment } from 'src/app/models/blueprints/path-to-live-environment';

@Component({
    selector: 'app-package-update-popup',
    templateUrl: './package-update-popup.component.html',
    styleUrls: ['./package-update-popup.component.scss'],
    standalone: false
})
export class PackageUpdatePopupComponent  implements OnInit {
private destroyRef = inject(DestroyRef);

  @Input() instance: any;
  @Input() viewOnly: any;
  expandedElement: any;
  dataSourceInner: any;
  dataSourceInnerPackages: any;
  innerTableColumns: string[] = ['name', 'path', 'tag'];
  dataLoading = true;

  externalPackages: any;
  filteredExternalPackages: any;
  selectedExternalPackage: any;
  dataSource: any;
  Constants = Constants;
  searchValue: string;

  constructor(public sidenavService: SidenavService, private solutionsService: SolutionsService,
    private toastrService: CustomToastrService, public dialog: MatDialog) {
  }
  
  ngOnInit(): void {
    this.getExternalPackages();
  }

  getExternalPackages(): void {
    this.solutionsService.getReferencePackages(this.instance.solutionId, this.instance.tenantId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(data => {
        if (data.total > 10) {
          const size = data.total
          this.solutionsService.getReferencePackages(this.instance.solutionId, this.instance.tenantId, size)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(results => {
              this.externalPackages = results?.data;
              if (this.instance.environment == PathToLiveEnvironment.LIVE) {
                this.externalPackages = this.externalPackages?.filter(item => item?.id === this.instance.preprodData);
              }
              this.filteredExternalPackages = this.externalPackages;
              this.dataLoading = false;
            });
        } else {
          this.externalPackages = data?.data;
          if (this.instance.environment == PathToLiveEnvironment.LIVE) {
            this.externalPackages = this.externalPackages?.filter(item => item?.id === this.instance.preprodData);
          }
          this.filteredExternalPackages = this.externalPackages;
          this.dataLoading = false;
        }
      },
      (error) => {
        this.dataLoading = false;
        setTimeout(() => {
		      const title = translate('ERRORS.SERVICE_AVAILABILITY');
          const message = error?.message ? error?.message : (error?.error?.message ? error?.error?.message : (error?.errmsg ? error?.errmsg : translate('SERVICE.INTERNAL_SERVER_ERROR')));
          this.toastrService.errMessage(message, title);
        }, 500);
    });
  }

  searchChanged(value): void {
    this.filteredExternalPackages = this.externalPackages.filter(item => item?.name?.toLowerCase().includes(value.toLowerCase()));
  }

  onClick(event) {
    if (this.filteredExternalPackages?.length === 0) {
      this.filteredExternalPackages = this.externalPackages;
      this.searchValue = undefined;
    }
  }

  externalPackageChanged(value): void {
    this.selectedExternalPackage = value;
    this.expandedElement = undefined;
    this.selectedExternalPackage.references.forEach(data => data.arrowClick = false);
    this.dataSource = new MatTableDataSource(this.selectedExternalPackage?.references);
  }

  onArrowClick(data): void {
    this.expandedElement = this.expandedElement === data ? null : data;
    this.selectedExternalPackage.references.find(item => item.component === data.component).arrowClick = !this.instance?.externalPackage?.references?.find(item => item.component === data.component)?.arrowClick;
    this.selectedExternalPackage?.references?.forEach(item =>  {
      if(item.component !== data.component) {
        item.arrowClick = false;
      }
    });
    this.dataSourceInner = this.selectedExternalPackage?.references?.find(item => item.component === data.component).images;
    this.dataSourceInnerPackages = this.selectedExternalPackage?.references?.find(item => item.component === data.component).packages;
  }

  updatePackage(): void {
    if (this.selectedExternalPackage) {
      // displaying warning popup for failed environment.Upon user confirmation, proceed with submission.
      if (this.instance.showWarningPopup) {
        this.dialog
          .open(ConfirmationDialogComponent, {
            panelClass: 'default-dialog-wrapper',
            width: '650px',
            autoFocus: false,
            data: {
              title: translate('INSTANCE_DETAILS.UPDATE_BUILD.WARNING_TITLE', {
                action: translate('INSTANCE_DETAILS.UPDATE_BUILD.CUSTOM_PACKAGE')}),
              message: translate('INSTANCE_DETAILS.UPDATE_BUILD.WARNING_NOTE', {
                action: translate('INSTANCE_DETAILS.UPDATE_BUILD.CUSTOM_PACKAGE'),
              }),
              confirmButtonText: translate('INSTANCE_DETAILS.UPDATE_BUILD.PROCEED'),
              warning: true
            },
          })
          .afterClosed()
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe((confirm) => {
            if (confirm) {
              this.updateCustomPackage();
            }
          });
      } else {
        this.updateCustomPackage();
      }
    } else {
      this.dialog.open(GenericErrorComponent, {
        minHeight: '342px',
        width: '566px',
        panelClass: 'flex-dialog'
      });
    }
  }
  updateCustomPackage(){
    this.dialog.open(ConfirmationDialogComponent, {
      panelClass: 'default-dialog-wrapper',
      width: '566px',
      autoFocus: false,
      data: {
        title: translate('INSTANCE_DETAILS.UPDATE_CUSTOM_PACKAGE'),
        message: translate('INSTANCE_DETAILS.UPDATE_CUSTOM_PACKAGE_DESC'),
        confirmButtonText: translate('BUTTONS.CONFIRM')
      }
    })
    .afterClosed()
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe(confirm => {
      if (confirm) {
        this.sidenavService.closeRightSidenav(this.selectedExternalPackage);
      }
    });
  }

}
